<template>
    <form-wrapper @submit="handleFormSubmission()">
        <practice-fragment
            v-show="!dataForPrefill.practiceName || invitee?.inviteType === JoinInviteTypeEnum.REFERRAL_CREATION"
            :highlightMissingFields="isPracticeMissingFields"
            :formData="data"
            :isDisabled="areInputsDisabledAfterFormIsSubmitted"
            :isPracticeTypeDisabled="updateDemo || !!$route.query.jit"
            :isPracticeSuggestable="!updateDemo"
            @change="change($event)"
        ></practice-fragment>
        <div v-show="updateDemo"></div>
        <br />
        <div v-show="data.practiceType">
            <form-row>
                <form-cell width="half">
                    <text-field
                        :label="$t('forms.auth.register.fields.first_name.label')"
                        :placeholder="$t('forms.auth.register.fields.first_name.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        :isDisabled="areInputsDisabledAfterFormIsSubmitted"
                        valuePath="firstName"
                        @change="change($event)"
                    ></text-field>
                </form-cell>

                <form-cell width="quarter">
                    <text-field
                        :label="$t('forms.auth.register.fields.middle_name.shortLabel')"
                        :placeholder="$t('forms.auth.register.fields.middle_name.placeholder')"
                        :isRequired="false"
                        :formData="data"
                        :isDisabled="areInputsDisabledAfterFormIsSubmitted"
                        valuePath="middleName"
                        @change="change($event)"
                    ></text-field>
                </form-cell>

                <form-cell width="half">
                    <text-field
                        :label="$t('forms.auth.register.fields.last_name.label')"
                        :placeholder="$t('forms.auth.register.fields.last_name.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        :isDisabled="areInputsDisabledAfterFormIsSubmitted"
                        valuePath="lastName"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <text-field
                        ref="registrationEmailInput"
                        :class="invalidInputEmailClass"
                        :label="$t('forms.auth.register.fields.email.label')"
                        :placeholder="$t('forms.auth.register.fields.email.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        :isDisabled="areInputsDisabledAfterFormIsSubmitted || updateDemo"
                        valuePath="email"
                        type="email"
                        @blur="checkEmailOnBlur"
                        @change="emailChange($event)"
                    >
                        <template #hint>
                            <p v-html="$t('forms.auth.register.fields.email.hint')"></p>
                        </template>

                        <template #error="{ error }">
                            <template v-if="error.code === 'entity.duplicate'">
                                {{ $t('forms.auth.register.errors.email.duplicate.message') }}
                                <router-link :to="{ name: 'auth.login' }">
                                    {{ $t('forms.auth.register.errors.email.duplicate.link') }}
                                </router-link>
                            </template>
                            <template v-else>
                                {{ error }}
                            </template>
                        </template>
                    </text-field>
                </form-cell>
            </form-row>

            <form-row v-if="!$route.query.jit && !updateDemo">
                <form-cell>
                    <text-field
                        ref="registrationEmailRepeatInput"
                        :class="invalidInputEmailRepeatClass"
                        :label="$t('forms.auth.register.fields.email.repeat_label')"
                        :placeholder="$t('forms.auth.register.fields.email.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        :isDisabled="areInputsDisabledAfterFormIsSubmitted"
                        valuePath="emailRepeat"
                        type="email"
                        :error="emailRepeatError"
                        @blur="checkEmailRepeatOnBlur"
                        @change="change($event), checkEmailRepeatOnBlur()"
                    >
                        <template #hint>
                            <p v-html="$t('forms.auth.register.fields.email.repeat_hint')"></p>
                        </template>

                        <template #error="{ error }">
                            {{ error }}
                        </template>
                    </text-field>
                </form-cell>
            </form-row>

            <form-row v-if="!fieldsToHide.includes('phone')">
                <form-cell>
                    <phone-field
                        :label="$t('forms.auth.register.fields.phone.label')"
                        :placeholder="$t('common.phone_number.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        :isDisabled="areInputsDisabledAfterFormIsSubmitted"
                        valuePath="phone"
                        :class="invalidInputPhoneClass"
                        @change="change($event)"
                        @country-code-change="prefillData($event)"
                    >
                        <template #hint>
                            <p v-html="$t('forms.auth.register.fields.phone.mobile_sms_hint')"></p>
                        </template>
                    </phone-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <text-field
                        ref="registrationPasswordInput"
                        :class="invalidInputPasswordClass"
                        :label="$t('forms.auth.register.fields.password.label')"
                        :placeholder="$t('forms.auth.register.fields.password.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        :isDisabled="areInputsDisabledAfterFormIsSubmitted"
                        valuePath="password"
                        :type="passwordInputFieldType"
                        :maxLength="40"
                        @blur="checkPasswordOnBlur"
                        @change="checkOnChange($event)"
                    >
                        <template #icon>
                            <common-toggle-visibility-button
                                @visibility-toggle-switch="showPassword"
                            ></common-toggle-visibility-button>
                        </template>
                    </text-field>
                </form-cell>
            </form-row>

            <form-row v-if="data.practiceType === PracticeTypeEnum.PHYSICIAN">
                <form-cell>
                    <select-field
                        :label="$t('forms.auth.register.fields.role.label')"
                        :placeholder="$t('forms.auth.register.fields.role.placeholder')"
                        :options="organizationRoles"
                        :isRequired="true"
                        :formData="data"
                        :isDisabled="areInputsDisabledAfterFormIsSubmitted || isUserRoleIdDisabled"
                        valuePath="userRoleId"
                        :error="userRoleError"
                        @change="roleChanged($event)"
                    ></select-field>
                </form-cell>
            </form-row>

            <form-row v-if="showPhysiciangroup && !fieldsToHide.includes('physicianGroupId')">
                <form-cell>
                    <select-field
                        :label="$t('forms.auth.register.fields.physicianGroup.label')"
                        :placeholder="$t('forms.auth.register.fields.physicianGroup.placeholder')"
                        :options="physicianGroups"
                        :isRequired="false"
                        :formData="data"
                        :isDisabled="true"
                        valuePath="physicianGroupId"
                        @change="change($event)"
                    ></select-field>
                </form-cell>
            </form-row>

            <form-row v-if="showPhysicianData">
                <form-cell v-if="!fieldsToHide.includes('designation')" width="third">
                    <select-field
                        :label="$t('forms.auth.register.fields.title.label')"
                        :placeholder="$t('forms.auth.register.fields.title.placeholder')"
                        :options="designationOptions"
                        :isRequired="true"
                        :formData="data"
                        :isDisabled="areInputsDisabledAfterFormIsSubmitted"
                        valuePath="designation"
                        @change="change($event)"
                    ></select-field>
                </form-cell>

                <form-cell>
                    <multi-select-field
                        :label="$t('forms.auth.register.fields.specialty.label')"
                        :placeholder="$t('forms.auth.register.fields.specialty.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        :options="specialities"
                        :error="specialitiesError"
                        :expandableOptions="true"
                        :oneOptionSelected="true"
                        :grayColor="true"
                        :isDisabled="areInputsDisabledAfterFormIsSubmitted"
                        valuePath="specialities"
                        @change="specialtyChanged($event)"
                    ></multi-select-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <checkbox-field
                        :formData="data"
                        :isDisabled="areInputsDisabledAfterFormIsSubmitted"
                        valuePath="agree"
                        @change="change($event)"
                    >
                        <template #label>
                            <span v-html="$t('forms.auth.register.fields.agree.label')"></span>
                        </template>
                    </checkbox-field>
                </form-cell>
            </form-row>
        </div>
        <confirm-practice-modal
            v-if="showConfirmPracticeModal"
            :userCanCreatePractice="userCanCreatePractice"
            :suggestedPractices="suggestedPractices"
            :data="data"
            @join-existing-practice="joinExistingPractice($event)"
            @register-new-practice="registerNewPractce($event)"
            @close="closeModal"
        ></confirm-practice-modal>

        <invite-admin-modal
            v-if="showInviteAdminModal"
            :formData="data"
            :inviteError="joinInviteId"
            valuePath="inviteEmail"
            @admin-invited="adminInvited($event)"
            @send-reminder="sendReminder($event)"
            @close="closeModal"
        ></invite-admin-modal>

        <template #footer>
            <common-buttons v-show="data.practiceType">
                <common-button
                    :isDisabled="!data.agree || isEmailInvalid || isEmailRepeatInvalid"
                    :isProcessing="isSending"
                    :label="submitLabel"
                    type="primary"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import Store from '~/store'
    import UserDesignationEnum from '../../enums/UserDesignationEnum'
    import FormMixin from 'mixins/FormMixin'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import UsersAPI from 'api/UsersAPI'
    import AuthAPI from 'api/AuthAPI'
    import ConfirmPracticeModal from '../page-elements/auth/AuthSignupConfirmPracticeModal'
    import InviteAdminModal from '../page-elements/auth/AuthSignupInviteAdminModal'
    import PracticesAPI from 'api/PracticesAPI'
    import GoogleAnalyticsService from 'services/GoogleAnalyticsService'
    import PasswordToggleVisibilityMixin from 'mixins/PasswordToggleVisibilityMixin'
    import UserRolesEnum from '../../enums/UserRolesEnum'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import AuthService from 'services/AuthService'
    import JoinInviteTypeEnum from '@/enums/JoinInviteTypeEnum'

    export default {
        mixins: [FormMixin, ScreenSizeDetectionMixin, PasswordToggleVisibilityMixin],

        components: {
            ConfirmPracticeModal,
            InviteAdminModal,
        },

        props: {
            organizationRoles: { type: Array, required: true },
            specialities: { type: Array, required: true },
            physicianGroups: { type: Array, required: true },
            dataForPrefill: { type: Object, required: true },
            isComingFromInvite: { type: Boolean, default: false },
            updateDemo: { type: Boolean, default: false },
            fieldsToHide: { type: Array, required: false, default: new Array() },
            invitee: { type: Object, required: false, default: null },
        },

        data() {
            return {
                showSpeciality: false,
                showConfirmPracticeModal: false,
                showInviteAdminModal: false,
                userRoleError: '',
                specialitiesError: '',
                emailRepeatError: '',
                joinInviteId: '',
                suggestedPractices: [],
                user: null,
                isPasswordInvalid: false,
                isEmailInvalid: false,
                isEmailRepeatInvalid: false,
                userRoles: UserRolesEnum,
                PracticeTypeEnum: PracticeTypeEnum,
                areInputsDisabledAfterFormIsSubmitted: false,
                isFormRejected: false,
                JoinInviteTypeEnum,
            }
        },

        computed: {
            showPhysicianData() {
                return (
                    (this.showSpeciality ||
                        [this.userRoles.PHYSICIAN.ID, this.userRoles.NURSE_PRACTITIONER.ID].includes(
                            this.dataForPrefill.userRoleId,
                        )) &&
                    this.data.practiceType !== PracticeTypeEnum.ANCILLARY
                )
            },

            designationOptions() {
                let designations = Object.values(UserDesignationEnum).map((designation) => ({
                    value: designation,
                    label: designation,
                }))
                if (this.data?.userRoleId === this.userRoles?.NURSE_PRACTITIONER.ID) {
                    return designations.filter(
                        (item) => item.value === UserDesignationEnum.PA || item.value === UserDesignationEnum.NP,
                    )
                }

                return designations.filter(
                    (item) => item.value !== UserDesignationEnum.PA && item.value !== UserDesignationEnum.NP,
                )
            },

            showPhysiciangroup() {
                return (
                    [this.userRoles.PHYSICIAN.ID, this.userRoles.NURSE_PRACTITIONER.ID].includes(this.data.userRoleId) ||
                    this.data.practiceType === PracticeTypeEnum.ANCILLARY
                )
            },

            submitLabel() {
                return this.isTablet ? this.$t('common.microcopy.next') : this.$t('forms.auth.register.microcopy.signup')
            },

            subSpecialities() {
                const merged = [].concat.apply(
                    [],
                    this.specialities.map((speciality) => speciality.options),
                )
                return merged.map((subSpeciality) => ({
                    specialityId: subSpeciality.data.optionId,
                    subSpecialityId: subSpeciality.data.id,
                }))
            },

            isComingFromNpi() {
                return !!this.$route.query.npi && Object.keys(this.dataForPrefill).length > 0
            },

            isInvitee() {
                return !!this.$route.query.jit && Object.keys(this.dataForPrefill).length > 0
            },

            isPracticeInvitee() {
                return this.invitee?.inviteType === JoinInviteTypeEnum.PRACTICE
            },

            isNetworkInvitee() {
                return this.invitee?.inviteType === JoinInviteTypeEnum.NETWORK
            },

            missingFields() {
                if (!this.isComingFromNpi && !this.isInvitee) return {}

                return {
                    practiceName: !this.dataForPrefill.practiceName,
                    middleName: !this.dataForPrefill.middleName,
                    emailRepeat: true, // Always missing from prefill
                    password: true, // Always missing from prefill
                    userRoleId: !this.dataForPrefill.userRoleId,
                    physicianGroupId: !this.dataForPrefill.physicianGroupId,
                    phone: !this.dataForPrefill.phone,
                }
            },

            invalidInputEmailClass() {
                return { 'form-input-text--is-invalid': this.isEmailInvalid }
            },

            invalidInputEmailRepeatClass() {
                return {
                    'form-input-text--is-invalid':
                        this.isEmailRepeatInvalid || (!this.data.emailRepeat && this.missingFields.emailRepeat),
                }
            },

            invalidInputPasswordClass() {
                return {
                    'form-input-text--is-invalid': this.isPasswordInvalid || (this.missingFields.password && !this.data.password),
                }
            },

            invalidInputPhoneClass() {
                return {
                    'form-input-text--is-invalid': this.missingFields.phone && !this.data.phone,
                }
            },

            isPracticeMissingFields() {
                return this.isComingFromNpi || this.isNetworkInvitee
            },

            userCanCreatePractice() {
                return (
                    this.data.userRoleId === this.userRoles.PHYSICIAN.ID ||
                    this.data.userRoleId === this.userRoles.NURSE_PRACTITIONER.ID ||
                    this.data.userRoleId === this.userRoles.OFFICE_MANAGER.ID ||
                    this.data.practiceType === PracticeTypeEnum.ANCILLARY
                )
            },

            isUserRoleIdDisabled() {
                return this.invitee?.inviteType === JoinInviteTypeEnum.PRACTICE && !!this.invitee?.userRoleId
            },

            isPhysicianGroupIdDisabled() {
                return (
                    (this.invitee?.inviteType === JoinInviteTypeEnum.PRACTICE && !!this.invitee?.physicianGroupId) ||
                    !!this.$route.query.groupId
                )
            },
        },

        methods: {
            createUser(data) {
                if (this.updateDemo) {
                    return this.handleRequest(UsersAPI.updateDemo(data))
                        .then(async (res) => {
                            GoogleAnalyticsService.emitRegistrationGoogleAnalytics(this.$ga)
                            if (this.isComingFromInvite) {
                                GoogleAnalyticsService.emitRegistrationByInviteGoogleAnalytics(this.$ga)
                            }
                            localStorage.setItem('findProviderUserId', res.id)
                            AuthService.logOut()
                            this.$router.push({ name: 'auth.signup.congratulations', query: { email: res.email, id: res.id } })
                        })
                        .catch((error) => {
                            this.areInputsDisabledAfterFormIsSubmitted = false
                            if (error?.response?.data?.subCode === 'user.user_role.join_invitation.conflict') {
                                this.isSending = false
                                this.userRoleError = this.$t(
                                    'forms.auth.register.fields.role.error.user_role_join_invitation_conflict',
                                    {
                                        email: data.email,
                                    },
                                )
                            }
                        })
                } else {
                    return this.handleRequest(UsersAPI.create(data))
                        .then(async (res) => {
                            this.isFormRejected = false
                            GoogleAnalyticsService.emitRegistrationGoogleAnalytics(this.$ga)
                            if (this.isComingFromInvite) {
                                GoogleAnalyticsService.emitRegistrationByInviteGoogleAnalytics(this.$ga)
                            }
                            localStorage.setItem('findProviderUserId', res.id)
                            this.$router.push({ name: 'auth.signup.congratulations', query: { email: res.email, id: res.id } })
                        })
                        .catch((error) => {
                            this.areInputsDisabledAfterFormIsSubmitted = false
                            this.isFormRejected = true
                            if (error?.response?.data?.subCode === 'user.user_role.join_invitation.conflict') {
                                this.isSending = false
                                this.userRoleError = this.$t(
                                    'forms.auth.register.fields.role.error.user_role_join_invitation_conflict',
                                    {
                                        email: data.email,
                                    },
                                )
                            }
                        })
                }
            },

            inviteUser(email) {
                UsersAPI.createMmaJoinPracticeInvites({
                    userId: localStorage.getItem('findProviderUserId'),
                    emails: [{ email: email }],
                    practiceName: this.data.practiceName,
                    practiceType: this.data.practiceType,
                }).then((res) => {
                    if (res?.emailStatuses[0]?.status !== 'sent') {
                        this.joinInviteId = `${res.emailStatuses[0].joinInviteId}`
                    } else {
                        Store.dispatch('ui/setAdminAlreadyInvited')
                        this.emitSent()
                    }
                })
            },

            createSession() {
                return AuthAPI.logIn(this.data.email, this.data.password).then((AuthenticationResult) => {
                    const { accessToken, idToken, refreshToken } = AuthenticationResult.signInUserSession
                    this.$auth.logIn(accessToken.jwtToken, idToken.jwtToken, refreshToken.token)
                })
            },

            createUserWithPractice(isNewPractice, practice) {
                const data = { ...this.data }
                if (isNewPractice) {
                    delete data.practiceId
                } else {
                    data.practiceId = practice.id
                }
                if (practice) data.practiceName = practice.name
                this.createUser(data).then((res) => {
                    if (res?.id) {
                        AuthAPI.sendVerificationEmail(res.id).then(() => {
                            this.isResendEmailProcessing = false
                            this.isConfirmationEmailSent = true
                        })
                    }
                })
            },

            checkUserRoleSelected() {
                if (!this.data.userRoleId) {
                    this.userRoleError = this.$t('forms.auth.register.errors.role')
                    return false
                }
                this.userRoleError = ''
                return true
            },

            checkSpecialtySelected() {
                if (this.showSpeciality) {
                    if (this.data.specialities && this.data.specialities.length) {
                        this.specialitiesError = ''
                        return true
                    }
                    this.specialitiesError = this.$t('forms.auth.register.errors.specialty')
                    return false
                }
                this.specialitiesError = ''
                return true
            },

            handleFormSubmission() {
                this.isSending = true
                if (this.data.physicianGroupId === -1) {
                    delete this.data.physicianGroupId
                }
                if (this.$route.query.npi) {
                    this.data.npiNumber = this.$route.query.npi
                }
                if (this.$route.query.invitedByUserId) {
                    this.data.invitedByUserId = +this.$route.query.invitedByUserId
                }
                if (this.data.specialities && this.data.specialities.length) {
                    this.data.userSpecialities = this.subSpecialities.filter((subSpeciality) =>
                        this.data.specialities.includes(subSpeciality.subSpecialityId),
                    )
                } else {
                    delete this.data.userSpecialities
                }

                if (
                    (this.checkUserRoleSelected() ||
                        this.data.practiceType === PracticeTypeEnum.ANCILLARY ||
                        this.data.practiceType === PracticeTypeEnum.OTHER) &&
                    this.checkSpecialtySelected()
                ) {
                    this.areInputsDisabledAfterFormIsSubmitted = true
                    this.emitEvent({ name: 'form-submitted', payload: { loginLinkDisabled: true } })
                    const practiceName = this.data.selectedPracticeName ? this.data.selectedPracticeName : this.data.practiceName
                    const practiceId = this.data.practiceId
                    PracticesAPI.getList(practiceName, this.data.practiceType).then((res) => {
                        this.suggestedPractices = res
                            .map((res) => res.data)
                            .filter((practice) => practice.name === practiceName)
                            .sort((x, y) => (x.id == practiceId ? -1 : y.id == practiceId ? 1 : 0))
                        if (this.suggestedPractices.length > 0 && !this.isPracticeInvitee) {
                            this.showConfirmPracticeModal = true
                            this.areInputsDisabledAfterFormIsSubmitted = false
                            this.isSending = false
                        } else if (
                            this.data.userRoleId !== UserRolesEnum.PHYSICIAN.ID &&
                            this.data.userRoleId !== UserRolesEnum.NURSE_PRACTITIONER.ID &&
                            this.data.userRoleId !== UserRolesEnum.OFFICE_MANAGER.ID &&
                            this.data.practiceType === PracticeTypeEnum.PHYSICIAN &&
                            !this.isPracticeInvitee
                        ) {
                            this.showInviteAdminModal = true
                            this.emitEvent({ name: 'form-submitted', payload: { loginLinkDisabled: false } })
                            this.areInputsDisabledAfterFormIsSubmitted = false
                            this.isSending = false
                        } else {
                            if (this.updateDemo) {
                                this.data.practiceName = this.data.name
                                this.createUser(this.data)
                                return
                            }
                            this.data.practiceType === PracticeTypeEnum.ANCILLARY && !this.isPracticeInvitee
                                ? this.createUser(this.data)
                                : this.createUserWithPractice(!this.isPracticeInvitee ? true : false, this.invitee?.practice)
                        }
                    })
                }
            },

            roleChanged(event) {
                this.userRoleError = ''
                this.change(event)
                this.change({ path: 'designation', value: null })
                if (event.value !== UserRolesEnum.PHYSICIAN.ID && event.value !== UserRolesEnum.NURSE_PRACTITIONER.ID) {
                    this.showSpeciality = false
                    this.change({ path: 'specialities', value: [] })
                    this.change({ path: 'designation', value: null })
                } else {
                    this.showSpeciality = true
                }
            },

            specialtyChanged(event) {
                this.specialitiesError = ''
                this.change(event)
            },

            closeModal() {
                this.showConfirmPracticeModal = false
                this.showInviteAdminModal = false
            },

            adminInvited(inviteEmail) {
                if (this.user) {
                    return this.inviteUser(inviteEmail)
                } else {
                    this.createUser(this.data)
                        .then((res) => {
                            this.closeModal()
                            if (this.isFormRejected) {
                                return
                            }
                            this.user = res
                            this.inviteUser(inviteEmail)
                        })
                        .then(() => {
                            if (
                                process.env.VUE_APP_ENV === 'production' &&
                                this.$auth.isUserOther() &&
                                !this.$auth.getUser().isNotificationSentOnRegister
                            ) {
                                UsersAPI.sendNewMemberRegisteredNotificationEmail()
                            }
                        })
                        .catch(() => {
                            this.showInviteAdminModal = false
                        })
                }
            },

            sendReminder() {
                UsersAPI.sendReminderForJoinInvite(this.user.id, this.joinInviteId, 'create-practice').then(() => {
                    Store.dispatch('ui/setAdminAlreadyInvited')
                    this.emitSent()
                })
            },

            registerNewPractce(practice) {
                this.createUserWithPractice(true, practice)
                this.showConfirmPracticeModal = false
            },

            joinExistingPractice(practice) {
                this.createUserWithPractice(false, practice)
                this.showConfirmPracticeModal = false
            },

            emailChange(event) {
                this.change(event)

                if (this.data.emailRepeat) {
                    this.checkEmailRepeatOnBlur()
                }
            },

            checkEmailOnBlur() {
                UsersAPI.checkRegistrationEmail({ email: this.data.email })
                    .then(() => {
                        this.isEmailInvalid = false
                        this.isSending = false
                        this.isSent = true
                        if (this.$validation.get('email')) {
                            this.$validation.clear()
                        }
                    })
                    .catch((error) => {
                        this.error = error.response
                        this.isEmailInvalid = true
                        this.isSending = false
                        this.triggerAutofocusInput()
                    })
            },

            checkEmailRepeatOnBlur() {
                const email = this.data.email || ''
                const emailRepeat = this.data.emailRepeat || ''
                if (!this.isEmailInvalid && email !== emailRepeat) {
                    this.isEmailRepeatInvalid = true
                    this.emailRepeatError = this.$t('forms.auth.register.errors.email.repeat.message')
                } else {
                    this.isEmailRepeatInvalid = false
                    this.emailRepeatError = null
                }
            },

            checkPasswordOnBlur() {
                this.handleRequest(UsersAPI.checkRegistrationPassword({ password: this.data.password }))
                    .then(() => {
                        this.isPasswordInvalid = false
                        this.isSending = false
                        this.isSent = true
                        if (this.$validation.get('password')) {
                            this.$validation.clear()
                        }
                    })
                    .catch((error) => {
                        this.error = error.response
                        this.isPasswordInvalid = true
                        this.isSending = false
                        this.triggerAutofocusInput()
                    })
            },

            checkOnChange(event) {
                if (event.value.length >= 10) {
                    this.isPasswordInvalid = false
                    if (this.$validation.get('password')) {
                        this.$validation.clear()
                    }
                }
                this.change(event)
            },
        },

        watch: {
            dataForPrefill() {
                this.prefillData(this.dataForPrefill)

                if (this.specialities && this.dataForPrefill.specialities) {
                    this.change({ path: 'specialities', value: this.dataForPrefill.specialities })
                }
            },
        },

        mounted() {
            //THIS IS ONLY FOR MVP
            const practiceType = this.$route.query.pt ? this.$route.query.pt : null
            this.prefillData({ practiceType: practiceType })

            const physicianGroupId = this.$route.query.groupId ? +this.$route.query.groupId : undefined

            this.prefillData({
                practiceName: this.$route.query.practiceName,
                firstName: this.$route.query.firstName,
                middleName: this.$route.query.middleName,
                lastName: this.$route.query.lastName,
                email: this.$route.query.email,
                phone: this.$route.query.phone,
                physicianGroupId: physicianGroupId,
            })
        },
    }
</script>
